import { CANCELLATION_POLICIES, CANCELLATION_POLICY_DAYS, FORTY_FIVE_DAYS_BEFORE_CHECK_IN, SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import { dateIsAfter, dateIsBefore, addDays, formatHeadlineCancellationPolicyStartForDisplay } from 'lib/datetime/dateUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import moment from 'moment'

export const ALL_FLEXIBLE_CANCELLATION_POLICIES: Array<App.CancellationPolicyType> = [
  'partner-policy-prior-to-check-in-twenty-one-days',
  'flexible-cancellation-forty-five-days',
  'flexible-cancellation-charter-twenty-one-days',
]

export const ALL_CHARTER_CANCELLATION_POLICIES: Array<App.CancellationPolicyType> = [
  'flexible-cancellation-charter-twenty-one-days',
]

export function isConnectionCancellationPolicy(cancellationPolicy: App.OrderCancellationPolicyTypes): cancellationPolicy is App.OrderConnectionCancellationPolicy {
  return 'policyCode' in cancellationPolicy
}

export function isCancellationPolicy(cancellationPolicy: App.OrderCancellationPolicyTypes): cancellationPolicy is App.OrderCancellationPolicy {
  return 'type' in cancellationPolicy
}

export function isCreditsOnlyCancellationPolicy(cancellationPolicy: App.OrderCancellationPolicyTypes | undefined): boolean {
  return !!(
    cancellationPolicy &&
    isCancellationPolicy(cancellationPolicy) &&
    cancellationPolicy.type === 'credit-only-prior-to-check-in-fourteen-days'
  )
}

export const isCharterCancellationPolicy = (
  cancellationPolicy: App.OrderCancellationPolicyTypes | undefined,
) => {
  return !!cancellationPolicy && isCancellationPolicy(cancellationPolicy) &&
    !!cancellationPolicy.type &&
    ALL_CHARTER_CANCELLATION_POLICIES.includes(cancellationPolicy.type)
}

export const isFlexibleCancellationPolicy = (
  cancellationPolicy: App.OrderCancellationPolicyTypes | undefined,
) => {
  return !!cancellationPolicy && isCancellationPolicy(cancellationPolicy) &&
    !!cancellationPolicy.type &&
    ALL_FLEXIBLE_CANCELLATION_POLICIES.includes(cancellationPolicy.type)
}

export function getCancellationPolicyLeadInDays(cancellationPolicy: App.OfferCancellationPolicy): number | undefined {
  if (!cancellationPolicy.type) {
    return undefined
  }

  if (isFlexibleCancellationPolicy(cancellationPolicy)) {
    return getFlexibleCancellationDays(cancellationPolicy?.type)
  }

  return CANCELLATION_POLICY_DAYS[cancellationPolicy.type] || 0
}

export function isWithinChangeOfMindPolicy(purchaseDate: string | Date, checkIn: string | Date) {
  // Change of mind policy is 7 days after purchase AND 14 days before check in.
  // e.g. if you purchase 12 days before check in, no change of mind
  // purchase 15 days before check in, you get 1 day change of mind
  // purchase 21 days before check in, you get 7 days change of mind
  // etc
  return dateIsAfter(addDays(new Date(purchaseDate), 7), new Date()) &&
    dateIsBefore(addDays(new Date(), 14), new Date(checkIn))
}

export function getFlexibleCancellationDays(cancellationPolicyType?: App.CancellationPolicyType): number {
  return cancellationPolicyType === CANCELLATION_POLICIES.FLEXIBLE_CANCELLATION_FORTY_FIVE_DAYS ?
    FORTY_FIVE_DAYS_BEFORE_CHECK_IN :
    TWENTY_ONE_DAYS_BEFORE_CHECK_IN
}

/**
 * Free cancellation under a `post-purchase-seven-days` policy ends at the earlier of two
 * deadlines: 7 days after purchase, or 21 days before check-in. `propertyTimezoneOffset`, when
 * given, is applied to both the purchase-based and check-in-based deadlines before adding/
 * subtracting days -- matches `OfferPropertyCancellationPolicies`'s own timezone handling exactly.
 */
export function getFreeCancellationStartDate(checkIn?: string | moment.Moment, purchaseDate?: string, propertyTimezoneOffset?: number): moment.Moment | undefined {
  if (!checkIn) return undefined

  const purchaseMoment = purchaseDate ? moment(purchaseDate) : moment()
  // Truthy check, not `!== undefined` -- matches `OfferPropertyCancellationPolicies`'s original
  // inline convention (and `isFreeLpcCancellationPolicy` below) exactly. A timezone offset of `0`
  // (UTC+0 properties: UK, Portugal, Iceland, Ghana) is intentionally treated the same as "no
  // offset given", since applying `.utcOffset(0)` can shift which calendar day `.add`/`.subtract`
  // treats as the boundary versus leaving the moment in the environment's default parsing offset.
  const startBasedOnToday = propertyTimezoneOffset
    ? purchaseMoment.clone().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
    : purchaseMoment.clone().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  const startBasedOnCheckIn = propertyTimezoneOffset
    ? moment(checkIn).utcOffset(propertyTimezoneOffset).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days')
    : moment(checkIn).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days')
  return moment.min(startBasedOnToday, startBasedOnCheckIn)
}

export function isPostPurchaseSevenDayCancellationActive(checkIn?: string, purchaseDate?: string): boolean {
  const startDate = getFreeCancellationStartDate(checkIn, purchaseDate)
  return !startDate || startDate.isAfter(moment())
}

/**
 * Single source of truth for the Flash offer-page free-cancellation gate -- previously defined
 * locally in `FlashCancellationPolicies.tsx` (the real rendered component), moved here so the
 * offer-page assistant snapshot builder can call the exact same function instead of maintaining a
 * separate mirror that could silently drift from what the page actually shows.
 */
export const showFreeCancellation = (
  checkInDate: string | moment.Moment | undefined,
  isChangePackage: boolean | undefined,
  cancellationPolicy: App.OfferCancellationPolicy | undefined,
): boolean => {
  if (isChangePackage || !cancellationPolicy) {
    return false
  }
  if (cancellationPolicy?.type !== 'post-purchase-seven-days') {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    return moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days').isAfter(moment().startOf('day'))
  }
}

/** See `showFreeCancellation` above -- same reasoning for moving this out of the component file. */
export const showFlexibleCancellation = (
  checkInDate: string | moment.Moment | undefined,
  isChangePackage: boolean | undefined,
  cancellationPolicy: App.OfferCancellationPolicy | undefined,
): boolean => {
  if (isChangePackage || !cancellationPolicy) {
    return false
  }
  if (!isFlexibleCancellationPolicy(cancellationPolicy)) {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    const days = getFlexibleCancellationDays(cancellationPolicy?.type)
    const withinChangeOfMind = isWithinChangeOfMindPolicy(new Date(), moment(checkInDate).toDate())
    return withinChangeOfMind || moment(checkInDate).subtract(days, 'days').isAfter(moment().startOf('day'))
  }
}

/**
 * Single source of truth for the Bedbank free-cancellation deadline date -- previously computed
 * locally in `BedbankPropertyCancellationPoliciesBase.tsx` (the real rendered component), moved
 * here so the offer-page assistant snapshot builder can call the exact same derivation instead of
 * maintaining a separate mirror.
 */
export function getBedbankFreeCancellationDeadline(
  cancellationPolicy: App.BedbankCancellationPolicy,
  dateFormattingLocale?: string,
): string | undefined {
  const policy = cancellationPolicy.policies[0]
  if (!policy) return undefined
  return formatHeadlineCancellationPolicyStartForDisplay(
    policy.start.toISOString(),
    policy.timezone,
    dateFormattingLocale ?? getCurrentIntl().locale,
  )
}

function isFreeLpcCancellationPolicy(
  cancellationPolicy: App.OfferCancellationPolicy,
  checkIn: moment.Moment | undefined,
  propertyTimezoneOffset?: number,
): 'free' | undefined {
  const days = CANCELLATION_POLICY_DAYS[cancellationPolicy.type!]
  if (!days) return
  const startBasedOnCheckIn = checkIn ? ((propertyTimezoneOffset ? checkIn.utcOffset(propertyTimezoneOffset).subtract(days, 'days') : checkIn.subtract(days, 'days'))) : undefined
  const startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let startDate: moment.Moment | undefined
  if (cancellationPolicy.type === 'post-purchase-seven-days') {
    if (!startBasedOnCheckIn) {
      startDate = startBasedOnToday
    } else {
      startDate = startBasedOnToday.isBefore(startBasedOnCheckIn) ? startBasedOnToday : startBasedOnCheckIn
    }
  } else {
    startDate = startBasedOnCheckIn
  }
  const refundable = (!startDate || startDate?.isAfter(moment()))
  if (refundable) return 'free'
}

/**
 * Single source of truth for whether a Bedbank rate's "Non-refundable" text should render --
 * previously duplicated inline in `BedbankCancellationPolicy.tsx` (the real rendered component),
 * moved here so the offer-page assistant snapshot builder can call the exact same gate instead of
 * maintaining a separate mirror that could silently drift.
 */
export function shouldShowBedbankNonRefundableText(
  rate: Pick<App.BedbankRate, 'refundable' | 'partiallyRefundable'>,
  showNonCancellation: boolean,
): boolean {
  return !rate.partiallyRefundable && !rate.refundable && showNonCancellation
}

export function isFreeOrFlexibleLpcCancellation(
  cancellationPolicy: App.OfferCancellationPolicy | undefined,
  checkIn: moment.Moment | undefined,
  propertyTimezoneOffset?: number,
): 'flexible' | 'free' | undefined {
  if (!cancellationPolicy?.type || cancellationPolicy.type == 'non-refundable') return
  const isFlexible = isFlexibleCancellationPolicy(cancellationPolicy)
  if (isFlexible) return 'flexible'
  return isFreeLpcCancellationPolicy(cancellationPolicy, checkIn, propertyTimezoneOffset)
}
